import { ENQUIRY_RESPONSE_TIME } from "../utils/whatsappQuote";
import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { readEnquiryConfirmation } from "../utils/enquiryConfirmation";
import "./EnquiryThankYou.css";

const resolveBrand = () => {
  const configured = process.env.REACT_APP_SITE_MODE || "";
  const host = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  if (host.includes("minibusgrab") || configured === "minibusgrab") {
    return { name: "MinibusGrab", className: "minibus", journeyLabel: "minibus journey" };
  }
  if (host.includes("cheapcoachgrab") || configured === "cheapcoachgrab") {
    return { name: "CheapCoachGrab", className: "coach", journeyLabel: "coach journey" };
  }
  return { name: "CheapCabGrab", className: "ccg", journeyLabel: "journey" };
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const SummaryItem = ({ label, value }) => {
  if (value === "" || value == null) return null;
  return (
    <div className="enquiry-thanks-summary-item">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
};

const EnquiryThankYou = () => {
  const location = useLocation();
  const brand = useMemo(resolveBrand, []);
  const confirmation = location.state?.confirmation || readEnquiryConfirmation();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `Enquiry received | ${brand.name}`;
    let robots = document.querySelector('meta[name="robots"]');
    const existingContent = robots?.getAttribute("content") || "";
    const created = !robots;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex,nofollow");
    return () => {
      document.title = previousTitle;
      if (created) robots.remove();
      else robots.setAttribute("content", existingContent);
    };
  }, [brand.name]);

  if (!confirmation) {
    return (
      <main className={`enquiry-thanks-page enquiry-thanks-${brand.className}`}>
        <section className="enquiry-thanks-card enquiry-thanks-empty">
          <div className="enquiry-thanks-mark">?</div>
          <p className="enquiry-thanks-brand">{brand.name}</p>
          <h1>No recent enquiry found</h1>
          <p>Please start a new quote or contact our team if you need help with an existing request.</p>
          <div className="enquiry-thanks-actions">
            <Link className="primary" to="/">Start a new quote</Link>
            <Link to="/contact">Contact our team</Link>
          </div>
        </section>
      </main>
    );
  }

  const routeAvailable = confirmation.pickup || confirmation.dropoff;

  return (
    <main className={`enquiry-thanks-page enquiry-thanks-${brand.className}`}>
      <section className="enquiry-thanks-card">
        <div className="enquiry-thanks-mark">✓</div>
        <p className="enquiry-thanks-brand">{confirmation.brand || brand.name}</p>
        <h1>Thank you for your enquiry</h1>
        <p className="enquiry-thanks-intro">
          {confirmation.contactFlowV2
            ? `We’ve received your ${brand.journeyLabel} request. Our team will check suitable operators, vehicle availability and fares, then contact you. ${ENQUIRY_RESPONSE_TIME}`
            : <>We’ve received your {brand.journeyLabel} request. Our team is checking today’s best available operators and prices, and will contact you shortly.</>}
        </p>

        {confirmation.refCode && (
          <div className="enquiry-thanks-reference">
            <span>Enquiry reference</span>
            <strong>{confirmation.refCode}</strong>
          </div>
        )}

        <section className="enquiry-thanks-summary" aria-label="Journey received">
          <div className="enquiry-thanks-summary-heading">
            <h2>Journey received</h2>
            <span>Keep this page for your records</span>
          </div>
          {routeAvailable && (
            <div className="enquiry-thanks-route">
              <div><span>Pickup</span><strong>{confirmation.pickup || "To be confirmed"}</strong></div>
              <b aria-hidden="true">→</b>
              <div><span>Destination</span><strong>{confirmation.dropoff || "To be confirmed"}</strong></div>
            </div>
          )}
          <div className="enquiry-thanks-summary-grid">
            <SummaryItem label="Travel date" value={formatDateTime(confirmation.pickupTime)} />
            <SummaryItem label="Passengers" value={confirmation.passengers} />
            <SummaryItem label="Bags" value={confirmation.bags} />
            <SummaryItem label="Vehicle option" value={confirmation.vehicle} />
            {confirmation.returnRequested && <SummaryItem label="Return journey" value="Requested — details to confirm" />}
          </div>
        </section>

        <div className="enquiry-thanks-next">
          <h2>What happens next?</h2>
          <div><b>1</b><span><strong>We check availability</strong><small>Our team reviews suitable operators and today’s pricing.</small></span></div>
          <div><b>2</b><span><strong>We contact you</strong><small>You’ll receive the best available option before anything is confirmed.</small></span></div>
        </div>

        <div className="enquiry-thanks-reassurance">
          <span>✓ No payment taken</span>
          <span>✓ Final fare confirmed before booking</span>
          <span>✓ Licensed operator options</span>
          <span>✓ Flight monitoring for airport journeys</span>
        </div>

        <div className="enquiry-thanks-actions">
          <Link className="primary" to="/">Start another quote</Link>
          <Link to="/contact">Contact our team</Link>
        </div>
      </section>
    </main>
  );
};

export default EnquiryThankYou;
